import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["layoutSelect", "widgetsContainer", "regionsContainer", "previewContainer"]
  static values = {
    layoutId: String,
    layoutsUrl: String,
    widgetsData: Array,
    useStructures: Boolean
  }

  currentStructure = null
  activeRegionIndex = null

  connect() {
    console.log('DynamicScreenForm: connect() called')
    console.log('DynamicScreenForm: layoutIdValue:', this.layoutIdValue, 'type:', typeof this.layoutIdValue)
    console.log('DynamicScreenForm: useStructuresValue:', this.useStructuresValue)
    console.log('DynamicScreenForm: layoutsUrlValue:', this.layoutsUrlValue)
    
    // Si hay un layout_id inicial (modo edición), cargar sus regiones
    // Usar setTimeout para asegurar que el DOM esté completamente cargado
    if (this.layoutIdValue && this.layoutIdValue !== '' && this.layoutIdValue !== 'null' && this.layoutIdValue !== null) {
      console.log('DynamicScreenForm: Has layoutIdValue, will load in 500ms')
      setTimeout(() => {
        console.log('DynamicScreenForm: Loading layout with ID:', this.layoutIdValue)
        this.loadLayout(String(this.layoutIdValue)) // Asegurar que sea string
      }, 500)
    } else {
      console.log('DynamicScreenForm: No layoutIdValue, waiting for user selection')
    }
  }

  widgetsDataValueChanged() {
    // Cuando cambia widgetsDataValue, si ya hay una estructura cargada, re-renderizar
    if (this.currentStructure) {
      let widgetsData = this.widgetsDataValue || []
      if (typeof widgetsData === 'string') {
        try {
          widgetsData = JSON.parse(widgetsData)
        } catch (e) {
          console.error('Error parsing widgetsDataValue:', e)
          widgetsData = []
        }
      }
      this.renderRegions(this.currentStructure, widgetsData)
    }
  }

  onLayoutChange(event) {
    const layoutId = event.target.value
    if (layoutId) {
      this.loadLayout(layoutId)
    } else {
      this.hideWidgets()
    }
  }

  async loadLayout(layoutId) {
    try {
      // Asegurar que layoutId sea string
      layoutId = String(layoutId)
      console.log('DynamicScreenForm: loadLayout called with layoutId:', layoutId, 'type:', typeof layoutId)
      
      let url
      if (this.useStructuresValue) {
        // Si estamos seleccionando estructuras (formulario de layouts), usar endpoint de estructuras
        // Construir URL directamente sin depender de layoutsUrlValue para evitar duplicados
        url = `/portal/digital-signage/structures/${layoutId}.json`
        console.log('DynamicScreenForm: Loading structure from:', url)
      } else {
        // Si estamos seleccionando layouts (formulario de screens), usar endpoint de layouts con formato JSON
        const layoutsUrl = this.layoutsUrlValue || '/portal/digital-signage/layouts'
        url = `${layoutsUrl}/${layoutId}.json`
        console.log('DynamicScreenForm: Loading layout from:', url)
      }
      
      console.log('DynamicScreenForm: Fetching URL:', url)
      const response = await fetch(url)
      console.log('DynamicScreenForm: Response status:', response.status)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('DynamicScreenForm: HTTP error! status:', response.status, 'body:', errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const structure = await response.json()
      console.log('DynamicScreenForm: Structure loaded successfully:', structure)
      
      this.currentStructure = structure
      
      // Parsear widgetsDataValue si es necesario
      let widgetsData = this.widgetsDataValue || []
      if (typeof widgetsData === 'string') {
        try {
          widgetsData = JSON.parse(widgetsData)
          console.log('DynamicScreenForm: Parsed widgetsData:', widgetsData)
        } catch (e) {
          console.error('DynamicScreenForm: Error parsing widgetsDataValue:', e)
          widgetsData = []
        }
      }
      
      console.log('DynamicScreenForm: Rendering preview and regions')
      
      // Mostrar el contenedor primero
      this.showWidgets()
      
      // Luego renderizar el contenido
      this.renderPreview(structure)
      this.renderRegions(structure, widgetsData)
    } catch (error) {
      console.error('DynamicScreenForm: Error loading layout:', error)
      console.error('DynamicScreenForm: Error details:', error.message, error.stack)
      this.hideWidgets()
    }
  }

  showWidgets() {
    if (this.hasWidgetsContainerTarget) {
      this.widgetsContainerTarget.classList.remove('hidden')
      console.log('DynamicScreenForm: Widgets container shown')
      // Asegurar que los targets estén disponibles
      console.log('DynamicScreenForm: Has previewContainerTarget:', this.hasPreviewContainerTarget)
      console.log('DynamicScreenForm: Has regionsContainerTarget:', this.hasRegionsContainerTarget)
    } else {
      console.error('DynamicScreenForm: widgetsContainerTarget not found!')
    }
  }

  hideWidgets() {
    this.widgetsContainerTarget.classList.add('hidden')
    this.regionsContainerTarget.innerHTML = ''
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.innerHTML = ''
    }
    this.currentStructure = null
  }

  renderPreview(structure) {
    if (!this.hasPreviewContainerTarget) {
      console.error('DynamicScreenForm: previewContainerTarget not found!')
      console.error('DynamicScreenForm: Available targets:', {
        layoutSelect: this.hasLayoutSelectTarget,
        widgetsContainer: this.hasWidgetsContainerTarget,
        regionsContainer: this.hasRegionsContainerTarget,
        previewContainer: this.hasPreviewContainerTarget
      })
      return
    }
    console.log('DynamicScreenForm: Rendering preview for structure')
    console.log('DynamicScreenForm: previewContainerTarget element:', this.previewContainerTarget)
    
    const regions = structure.config.regions || []
    const previewHtml = `
      <div class="structure-preview" style="position: relative; width: 200px; height: 150px; border: 2px solid #e5e7eb; background: #f9fafb; border-radius: 8px; overflow: hidden;">
        ${regions.map(region => {
          const pos = region.position || {}
          const isActive = this.activeRegionIndex === region.index
          return `
            <div 
              class="preview-region ${isActive ? 'preview-region-active' : ''}"
              data-region-index="${region.index}"
              style="
                position: absolute;
                top: ${pos.top || 0}%;
                left: ${pos.left || 0}%;
                width: ${pos.width || 100}%;
                height: ${pos.height || 100}%;
                border: 2px solid ${isActive ? '#3b82f6' : '#9ca3af'};
                background: ${isActive ? 'rgba(59, 130, 246, 0.1)' : 'rgba(156, 163, 175, 0.1)'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                font-weight: ${isActive ? 'bold' : 'normal'};
                color: ${isActive ? '#3b82f6' : '#6b7280'};
                transition: all 0.2s;
                cursor: pointer;
              "
              title="${region.name || `Región ${region.index}`}"
            >
              <span class="text-center px-1">${region.name || `R${region.index}`}</span>
            </div>
          `
        }).join('')}
      </div>
    `
    
    this.previewContainerTarget.innerHTML = previewHtml
    
    // Agregar event listeners para resaltar región al hacer hover
    this.previewContainerTarget.querySelectorAll('.preview-region').forEach(regionEl => {
      regionEl.addEventListener('mouseenter', () => {
        const regionIndex = parseInt(regionEl.dataset.regionIndex)
        this.highlightRegion(regionIndex)
      })
      regionEl.addEventListener('mouseleave', () => {
        this.clearHighlight()
      })
    })
  }

  highlightRegion(regionIndex) {
    this.activeRegionIndex = regionIndex
    if (this.currentStructure) {
      this.renderPreview(this.currentStructure)
    }
    // También resaltar la sección del formulario
    const formRegion = document.querySelector(`[data-region-index="${regionIndex}"]`)
    if (formRegion) {
      formRegion.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      formRegion.classList.add('ring-2', 'ring-blue-500')
    }
  }

  clearHighlight() {
    this.activeRegionIndex = null
    if (this.currentStructure) {
      this.renderPreview(this.currentStructure)
    }
    document.querySelectorAll('[data-region-index]').forEach(el => {
      el.classList.remove('ring-2', 'ring-blue-500')
    })
  }

  renderRegions(structure, existingWidgets = []) {
    if (!this.hasRegionsContainerTarget) {
      console.error('DynamicScreenForm: regionsContainerTarget not found!')
      return
    }
    
    console.log('DynamicScreenForm: Rendering regions for structure:', structure)
    const regions = structure.config.regions || []
    const widgets = structure.available_widgets || []
    console.log('DynamicScreenForm: Regions:', regions.length, 'Widgets available:', widgets.length, 'Existing widgets:', existingWidgets.length)
    
    // Asegurar que existingWidgets es un array
    // Si es un string JSON, parsearlo
    if (typeof existingWidgets === 'string') {
      try {
        existingWidgets = JSON.parse(existingWidgets)
      } catch (e) {
        console.error('Error parsing widgetsData:', e)
        existingWidgets = []
      }
    }
    if (!Array.isArray(existingWidgets)) {
      existingWidgets = []
    }
    
    // Agrupar widgets existentes por región
    const widgetsByRegion = {}
    existingWidgets.forEach(w => {
      const regionIndex = w.region_index || w.regionIndex
      if (!widgetsByRegion[regionIndex]) {
        widgetsByRegion[regionIndex] = []
      }
      widgetsByRegion[regionIndex].push(w)
    })
    
    let html = ''
    
    regions.forEach((region, index) => {
      const regionIndex = region.index
      const regionWidgets = widgetsByRegion[regionIndex] || []
      
      html += `
        <div class="mb-6 border rounded-lg p-4 transition-all" 
             data-region-index="${regionIndex}"
             onmouseenter="this.dispatchEvent(new CustomEvent('region-hover', {detail: {regionIndex: ${regionIndex}}}))"
             onmouseleave="this.dispatchEvent(new CustomEvent('region-leave', {detail: {regionIndex: ${regionIndex}}}))">
          <div class="flex items-center justify-between mb-3">
            <h4 class="font-semibold">${region.name || `Región ${regionIndex}`}</h4>
            <button type="button" 
                    class="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    data-action="click->dynamic-screen-form#addWidget"
                    data-region-index="${regionIndex}">
              + Añadir Widget
            </button>
          </div>
          
          <div class="widgets-list-${regionIndex}" data-widgets-list="${regionIndex}">
            ${this.renderWidgetsForRegion(regionIndex, regionWidgets, widgets)}
          </div>
        </div>
      `
    })
    
    this.regionsContainerTarget.innerHTML = html
    
    // Agregar event listeners para resaltar preview al hacer hover en formulario
    this.regionsContainerTarget.querySelectorAll('[data-region-index]').forEach(regionEl => {
      regionEl.addEventListener('mouseenter', (e) => {
        const regionIndex = parseInt(regionEl.dataset.regionIndex)
        this.highlightRegion(regionIndex)
      })
      regionEl.addEventListener('mouseleave', () => {
        this.clearHighlight()
      })
    })
  }

  renderWidgetsForRegion(regionIndex, existingWidgets, availableWidgets) {
    if (existingWidgets.length === 0) {
      return '<p class="text-sm text-gray-500 italic">No hay widgets configurados. Haz clic en "+ Añadir Widget" para agregar uno.</p>'
    }
    
    return existingWidgets.map((widget, widgetIndex) => {
      const widgetObj = availableWidgets.find(w => w.id == widget.widget_id)
      return `
        <div class="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-widget-item="${regionIndex}-${widgetIndex}">
          <div class="flex items-center justify-between mb-3">
            <h5 class="font-medium text-gray-900">Widget ${widgetIndex + 1}</h5>
            <button type="button" 
                    class="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    data-action="click->dynamic-screen-form#removeWidget"
                    data-region-index="${regionIndex}"
                    data-widget-index="${widgetIndex}">
              - Eliminar
            </button>
          </div>
          
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Widget
            </label>
            <select 
              name="layout_widgets_data[${regionIndex}][${widgetIndex}][widget_id]"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 widget-select"
              data-region-index="${regionIndex}"
              data-widget-index="${widgetIndex}"
              data-action="change->dynamic-screen-form#onWidgetChange">
              <option value="">Selecciona un widget</option>
              ${availableWidgets.map(w => `
                <option value="${w.id}" ${widget.widget_id == w.id ? 'selected' : ''}>
                  ${w.name}
                </option>
              `).join('')}
            </select>
          </div>
          
          <div class="widget-fields-${regionIndex}-${widgetIndex}" data-widget-fields="${regionIndex}-${widgetIndex}">
            ${widgetObj ? this.renderWidgetFields(widgetObj, widget.data || {}, regionIndex, widgetIndex) : ''}
          </div>
          
          <input type="hidden" name="layout_widgets_data[${regionIndex}][${widgetIndex}][region_index]" value="${regionIndex}">
          <input type="hidden" name="layout_widgets_data[${regionIndex}][${widgetIndex}][position]" value="${widget.position || widgetIndex}">
        </div>
      `
    }).join('')
  }

  addWidget(event) {
    const regionIndex = parseInt(event.currentTarget.dataset.regionIndex)
    const widgetsList = document.querySelector(`[data-widgets-list="${regionIndex}"]`)
    const availableWidgets = this.currentStructure?.available_widgets || []
    
    // Contar widgets existentes en esta región
    const existingCount = widgetsList.querySelectorAll('[data-widget-item]').length
    
    // Crear nuevo widget vacío
    const newWidgetHtml = `
      <div class="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-widget-item="${regionIndex}-${existingCount}">
        <div class="flex items-center justify-between mb-3">
          <h5 class="font-medium text-gray-900">Widget ${existingCount + 1}</h5>
          <button type="button" 
                  class="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  data-action="click->dynamic-screen-form#removeWidget"
                  data-region-index="${regionIndex}"
                  data-widget-index="${existingCount}">
            - Eliminar
          </button>
        </div>
        
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Widget
          </label>
          <select 
            name="layout_widgets_data[${regionIndex}][${existingCount}][widget_id]"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 widget-select"
            data-region-index="${regionIndex}"
            data-widget-index="${existingCount}"
            data-action="change->dynamic-screen-form#onWidgetChange">
            <option value="">Selecciona un widget</option>
            ${availableWidgets.map(w => `
              <option value="${w.id}">${w.name}</option>
            `).join('')}
          </select>
        </div>
        
        <div class="widget-fields-${regionIndex}-${existingCount}" data-widget-fields="${regionIndex}-${existingCount}">
        </div>
        
        <input type="hidden" name="layout_widgets_data[${regionIndex}][${existingCount}][region_index]" value="${regionIndex}">
        <input type="hidden" name="layout_widgets_data[${regionIndex}][${existingCount}][position]" value="${existingCount}">
      </div>
    `
    
    // Si no hay widgets, reemplazar el mensaje; si hay, agregar al final
    if (widgetsList.textContent.includes('No hay widgets')) {
      widgetsList.innerHTML = newWidgetHtml
    } else {
      widgetsList.insertAdjacentHTML('beforeend', newWidgetHtml)
    }
  }

  removeWidget(event) {
    const regionIndex = parseInt(event.currentTarget.dataset.regionIndex)
    const widgetIndex = parseInt(event.currentTarget.dataset.widgetIndex)
    const widgetItem = document.querySelector(`[data-widget-item="${regionIndex}-${widgetIndex}"]`)
    
    if (widgetItem) {
      widgetItem.remove()
      
      // Reindexar widgets restantes
      this.reindexWidgets(regionIndex)
    }
  }

  reindexWidgets(regionIndex) {
    const widgetsList = document.querySelector(`[data-widgets-list="${regionIndex}"]`)
    const widgets = widgetsList.querySelectorAll('[data-widget-item]')
    
    widgets.forEach((widget, newIndex) => {
      // Actualizar índices en los atributos
      widget.setAttribute('data-widget-item', `${regionIndex}-${newIndex}`)
      
      // Actualizar nombres de campos
      widget.querySelectorAll('input, select, textarea').forEach(input => {
        if (input.name) {
          input.name = input.name.replace(
            new RegExp(`\\[${regionIndex}\\]\\[\\d+\\]`),
            `[${regionIndex}][${newIndex}]`
          )
        }
        if (input.dataset.widgetIndex !== undefined) {
          input.dataset.widgetIndex = newIndex
        }
      })
      
      // Actualizar data-widget-fields
      widget.querySelectorAll('[data-widget-fields]').forEach(el => {
        el.setAttribute('data-widget-fields', `${regionIndex}-${newIndex}`)
        el.className = el.className.replace(/widget-fields-\d+-\d+/, `widget-fields-${regionIndex}-${newIndex}`)
      })
      
      // Actualizar texto "Widget N"
      const title = widget.querySelector('h5')
      if (title) {
        title.textContent = `Widget ${newIndex + 1}`
      }
      
      // Actualizar position hidden input
      const positionInput = widget.querySelector('input[name*="[position]"]')
      if (positionInput) {
        positionInput.value = newIndex
      }
    })
    
    // Si no quedan widgets, mostrar mensaje
    if (widgets.length === 0) {
      widgetsList.innerHTML = '<p class="text-sm text-gray-500 italic">No hay widgets configurados. Haz clic en "+ Añadir Widget" para agregar uno.</p>'
    }
  }

  onWidgetChange(event) {
    const regionIndex = parseInt(event.target.dataset.regionIndex)
    const widgetIndex = parseInt(event.target.dataset.widgetIndex || '0')
    const widgetId = event.target.value
    const fieldsContainer = document.querySelector(`[data-widget-fields="${regionIndex}-${widgetIndex}"]`)
    
    if (!fieldsContainer) {
      console.error('Fields container not found for region', regionIndex, 'widget', widgetIndex)
      return
    }
    
    if (!widgetId) {
      fieldsContainer.innerHTML = ''
      return
    }
    
    // Cargar metadata del widget
    const layoutsUrl = this.layoutsUrlValue || '/portal/digital-signage/layouts'
    const widgetsUrl = layoutsUrl.replace('/layouts', '/widgets')
    fetch(`${widgetsUrl}/${widgetId}.json`)
      .then(response => response.json())
      .then(widget => {
        fieldsContainer.innerHTML = this.renderWidgetFields(widget, {}, regionIndex, widgetIndex)
      })
      .catch(error => {
        console.error('Error loading widget:', error)
        fieldsContainer.innerHTML = '<p class="text-red-600 text-sm">Error cargando widget</p>'
      })
  }

  renderWidgetFields(widget, existingData = {}, regionIndex, widgetIndex = 0) {
    if (!widget || !widget.metadata || !widget.metadata.fields) {
      return ''
    }
    
    const fields = widget.metadata.fields
    let html = '<div class="space-y-3 mt-3">'
    
    fields.forEach(field => {
      const fieldName = `layout_widgets_data[${regionIndex}][${widgetIndex}][data][${field.name}]`
      const fieldValue = existingData[field.name] || existingData[String(field.name)] || ''
      const required = field.required ? 'required' : ''
      
      html += `<div>`
      html += `<label class="block text-sm font-medium text-gray-700 mb-1">`
      // Obtener locale desde window o usar 'es' por defecto
      const locale = window.I18n?.locale || document.documentElement.lang || 'es'
      const fieldDescription = field.description?.[locale] || field.description?.['es'] || field.name
      html += `${fieldDescription}`
      if (field.required) {
        html += ` <span class="text-red-600">*</span>`
      }
      html += `</label>`
      
      switch (field.type) {
        case 'text':
          html += `<input type="text" name="${fieldName}" value="${fieldValue}" 
                         class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                         ${required}>`
          break
        case 'textarea':
          html += `<textarea name="${fieldName}" rows="3" 
                           class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                           ${required}>${fieldValue}</textarea>`
          break
        case 'number':
          html += `<input type="number" name="${fieldName}" value="${fieldValue}" 
                         class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                         ${required}>`
          break
        case 'array':
          // Para zen_content (array de URLs)
          const arrayValue = Array.isArray(fieldValue) ? fieldValue : (fieldValue ? [fieldValue] : [])
          html += `<div class="space-y-2" data-array-fields="${regionIndex}-${widgetIndex}-${field.name}">`
          arrayValue.forEach((item, index) => {
            html += `<div class="flex gap-2">`
            html += `<input type="text" name="${fieldName}[]" value="${item}" 
                           class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                           placeholder="URL">`
            html += `<button type="button" class="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700" 
                           onclick="this.parentElement.remove()">-</button>`
            html += `</div>`
          })
          html += `<button type="button" class="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm" 
                     onclick="window.addArrayField('${regionIndex}', '${widgetIndex}', '${field.name}', '${fieldName}')">+ Añadir</button>`
          html += `</div>`
          break
        default:
          html += `<input type="text" name="${fieldName}" value="${fieldValue}" 
                         class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                         ${required}>`
      }
      
      html += `</div>`
    })
    
    html += '</div>'
    return html
  }
}

// Helper global para añadir campos array (se llama desde el HTML generado)
if (typeof window !== 'undefined') {
  window.addArrayField = function(regionIndex, widgetIndex, fieldName, fieldNameBase) {
    const container = document.querySelector(`[data-array-fields="${regionIndex}-${widgetIndex}-${fieldName}"]`)
    if (!container) return
    
    const newField = document.createElement('div')
    newField.className = 'flex gap-2'
    newField.innerHTML = `
      <input type="text" name="${fieldNameBase}[]" value="" 
             class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
             placeholder="URL">
      <button type="button" class="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700" 
             onclick="this.parentElement.remove()">-</button>
    `
    // Insertar antes del último botón (el botón "Añadir")
    const buttons = container.querySelectorAll('button')
    const addButton = buttons[buttons.length - 1]
    if (addButton) {
      container.insertBefore(newField, addButton)
    } else {
      container.appendChild(newField)
    }
  }
}
